import { Controller } from "@hotwired/stimulus"
import SignaturePad from "signature_pad"

export default class extends Controller {
  static targets = [
    "canvas",
    "input",
    "placeholder",
    "statusBadge",
    "statusIcon",
    "statusLabel",
    "liveRegion",
    "errorContainer",
    "previewModal",
    "previewImage"
  ]

  // Textes UI passés depuis le serveur via I18n (data-signature-*-value).
  // Les defaults conservent l'ancien comportement si la vue n'expose pas le data-*.
  static values = {
    clearedText:    { type: String, default: "Signature effacée" },
    emptyError:     { type: String, default: "Veuillez dessiner votre signature avant de continuer" },
    emptyAnnounce:  { type: String, default: "Erreur : signature vide" },
    previewOpened:  { type: String, default: "Aperçu de la signature affiché" },
    previewClosed:  { type: String, default: "Aperçu fermé" },
    submitting:     { type: String, default: "Signature confirmée, envoi en cours" },
    signedText:     { type: String, default: "Signé" },
    unsignedText:   { type: String, default: "Non signé" }
  }

  connect() {
    this.signaturePad = new SignaturePad(this.canvasTarget, {
      backgroundColor: "rgb(255, 255, 255)",
      penColor: "rgb(0, 0, 0)",
      minWidth: 1.5,
      maxWidth: 2.5,
      velocityFilterWeight: 0.7
    })
    this.boundResizeCanvas = this.resizeCanvas.bind(this)
    this.resizeCanvas()
    window.addEventListener("resize", this.boundResizeCanvas)

    this.signaturePad.addEventListener("beginStroke", () => {
      this.hidePlaceholder()
      this.updateStatus(true)
      this.addInkRipple()
    })

    this.signaturePad.addEventListener("endStroke", () => {
      this.celebrateStroke()
    })
  }

  disconnect() {
    if (this.boundResizeCanvas) {
      window.removeEventListener("resize", this.boundResizeCanvas)
    }
  }

  resizeCanvas() {
    const ratio = Math.max(window.devicePixelRatio || 1, 1)
    this.canvasTarget.width = this.canvasTarget.offsetWidth * ratio
    this.canvasTarget.height = this.canvasTarget.offsetHeight * ratio
    this.canvasTarget.getContext("2d").scale(ratio, ratio)
    this.signaturePad.clear()
  }

  clear() {
    this.signaturePad.clear()
    this.inputTarget.value = ""
    this.showPlaceholder()
    this.updateStatus(false)
    this.announceLive(this.clearedTextValue)
    this.canvasTarget.focus()
  }

  hidePlaceholder() {
    if (this.hasPlaceholderTarget) {
      this.placeholderTarget.classList.add("hidden")
    }
  }

  showPlaceholder() {
    if (this.hasPlaceholderTarget) {
      this.placeholderTarget.classList.remove("hidden")
    }
  }

  addInkRipple() {
    this.canvasTarget.classList.add("signature-active")
    setTimeout(() => {
      this.canvasTarget.classList.remove("signature-active")
    }, 300)
  }

  celebrateStroke() {
    if (this.hasStatusIconTarget) {
      this.statusIconTarget.classList.add("signature-pulse")
      setTimeout(() => {
        this.statusIconTarget.classList.remove("signature-pulse")
      }, 600)
    }
  }

  updateStatus(isSigned) {
    if (!this.hasStatusLabelTarget) return

    const badge = this.hasStatusBadgeTarget
      ? this.statusBadgeTarget
      : this.statusLabelTarget.parentElement

    if (isSigned) {
      this.statusLabelTarget.textContent = this.signedTextValue
      badge.classList.remove("student-status-unsigned", "bg-gray-100")
      badge.classList.add("student-status-signed", "bg-green-100")
    } else {
      this.statusLabelTarget.textContent = this.unsignedTextValue
      badge.classList.remove("student-status-signed", "bg-green-100")
      badge.classList.add("student-status-unsigned", "bg-gray-100")
    }
  }

  announceLive(message) {
    if (this.hasLiveRegionTarget) {
      this.liveRegionTarget.textContent = message
    }
  }

  showError(message) {
    if (this.hasErrorContainerTarget) {
      const notification = document.createElement("div")
      notification.className = "bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded-md flex items-center gap-2 mb-4"
      notification.setAttribute("role", "alert")
      notification.innerHTML = `
        <i class="fas fa-exclamation-circle"></i>
        <span>${message}</span>
      `
      this.errorContainerTarget.innerHTML = ""
      this.errorContainerTarget.appendChild(notification)
      setTimeout(() => notification.remove(), 5000)
    }

    this.canvasTarget.classList.add("border-red-500")
    setTimeout(() => {
      this.canvasTarget.classList.remove("border-red-500")
    }, 3000)
  }

  save(event) {
    event.preventDefault()

    if (this.signaturePad.isEmpty()) {
      this.inputTarget.value = ""
      this.showError(this.emptyErrorValue)
      this.announceLive(this.emptyAnnounceValue)
      this.canvasTarget.focus()
      return
    }

    this.showPreview()
  }

  showPreview() {
    const originalDataUrl = this.signaturePad.toDataURL()
    const img = new Image()

    img.onload = () => {
      const targetWidth = 600
      const ratio = img.height / img.width
      const targetHeight = Math.round(targetWidth * ratio)

      const offscreenCanvas = document.createElement("canvas")
      offscreenCanvas.width = targetWidth
      offscreenCanvas.height = targetHeight
      const ctx = offscreenCanvas.getContext("2d")

      ctx.drawImage(img, 0, 0, targetWidth, targetHeight)

      const imageData = ctx.getImageData(0, 0, targetWidth, targetHeight)
      const data = imageData.data
      for (let i = 0; i < data.length; i += 4) {
        const avg = (data[i] + data[i + 1] + data[i + 2]) / 3
        data[i] = data[i + 1] = data[i + 2] = avg
      }
      ctx.putImageData(imageData, 0, 0)

      const grayResizedDataUrl = offscreenCanvas.toDataURL("image/png")
      this.inputTarget.value = grayResizedDataUrl

      if (this.hasPreviewImageTarget) {
        this.previewImageTarget.src = grayResizedDataUrl
      }

      if (this.hasPreviewModalTarget) {
        this.previewModalTarget.classList.remove("hidden")
        this.announceLive(this.previewOpenedValue)
      }
    }

    img.src = originalDataUrl
  }

  cancelPreview() {
    if (this.hasPreviewModalTarget) {
      this.previewModalTarget.classList.add("hidden")
      this.announceLive(this.previewClosedValue)
    }
  }

  confirmAndSubmit() {
    this.announceLive(this.submittingValue)
    if (this.hasPreviewModalTarget) {
      this.previewModalTarget.classList.add("hidden")
    }
    this.element.submit()
  }

  closeOnBackdrop(event) {
    if (event.target === this.previewModalTarget) {
      this.cancelPreview()
    }
  }

  preventPropagation(event) {
    event.stopPropagation()
  }
}
